import React, { useMemo } from "react";
import { useReadClientsApiClientsGet } from "@/type-gen/api";
import { useDebounce } from "use-debounce";
import { AutoComplete, type Option } from "@/shared/ui/autocomplete";
import { cn } from "@/lib/utils";

interface ClientAutoCompleteProps {
  value: string | null; // client_id or null
  displayValue: string | null; // client name to display
  onChange: (clientId: string | null, clientName: string | null) => void;
  onNewClientNameChange: (name: string) => void;
  newClientName: string;
  className?: string;
  error?: string | undefined;
}

export const ClientAutoComplete: React.FC<ClientAutoCompleteProps> = ({
  value,
  displayValue,
  onChange,
  onNewClientNameChange,
  newClientName,
  className,
  error,
}) => {
  // Track search term for API queries (based on user input)
  // Use newClientName as the source of truth for the search term
  const searchTerm =
    newClientName || (displayValue && !value ? displayValue : "");
  const [debouncedSearch] = useDebounce(searchTerm, 300);

  // Fetch clients based on search
  const { data: clientsData, isLoading } = useReadClientsApiClientsGet(
    {
      page: 1,
      page_size: 50,
      search: debouncedSearch?.trim() ? debouncedSearch : null,
    },
    {
      query: {
        enabled: !!debouncedSearch && debouncedSearch.trim().length > 0,
      },
    },
  );

  const clients = useMemo(
    () => clientsData?.data?.items ?? [],
    [clientsData?.data?.items],
  );

  // Convert clients to options for autocomplete
  const options: Option[] = useMemo(
    () =>
      clients.map((client) => ({
        value: client.id,
        label: client.description
          ? `${client.name} (${client.description})`
          : client.name,
      })),
    [clients],
  );

  // Convert current value to Option format
  const selectedOption: Option | undefined = useMemo(() => {
    if (value && displayValue) {
      return {
        value: value,
        label: displayValue,
      };
    }
    return undefined;
  }, [value, displayValue]);

  // Handle value change from autocomplete
  const handleValueChange = (option: Option | null) => {
    if (option) {
      // Check if it's an existing client or a new custom value
      const existingClient = clients.find((c) => c.id === option.value);
      if (existingClient) {
        // Existing client selected
        onChange(existingClient.id, existingClient.name);
        onNewClientNameChange("");
      } else {
        // Custom value entered
        onChange(null, null);
        onNewClientNameChange(option.label);
      }
    } else {
      // Value cleared
      onChange(null, null);
      onNewClientNameChange("");
    }
  };

  // Handle input change to track new client name
  const handleInputChange = (inputValue: string) => {
    // If the input doesn't match the selected client, clear selection
    if (value && displayValue && inputValue !== displayValue) {
      onChange(null, null);
    }
    onNewClientNameChange(inputValue);
  };

  return (
    <div className={cn("space-y-2", className)}>
      <AutoComplete
        options={options}
        value={selectedOption}
        onValueChange={handleValueChange}
        onInputChange={handleInputChange}
        isLoading={isLoading}
        placeholder="Type to search or create new client..."
        allowCustomValue={true}
        className={cn(
          error && "[&_input]:border-red-500 [&_input]:focus:ring-red-500",
        )}
      />
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
};
